import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { logException } from 'App/logging';
import { HomeAssetToken } from './__generated__/HomeAssetToken';
import { HOME_ASSET_TOKEN_QUERY } from './assetTokenQuery';

/**
 * Shared asset token for homepage sections that render Cloudinary thumbnails.
 *
 * `getAssetToken` is not normalisable, so folding it into every section query
 * would refetch the same token once per section. One `cache-first` query keeps
 * it to a single request no matter how many sections need it.
 *
 * Loading and error are not surfaced to the UI: without a token the consuming
 * tiles fall back to their placeholder icon. A failure is terminal for the
 * page's lifetime (`cache-first`, no retry affordance) and strips every
 * thumbnail at once, so it is logged for Sentry visibility — once per
 * consuming section, since each call is its own `useQuery` instance.
 */
export const useAssetToken = () => {
  const { data, error } = useQuery<HomeAssetToken>(HOME_ASSET_TOKEN_QUERY, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (error) {
      logException(error, { tags: { homeSection: 'asset-token' } });
    }
  }, [error]);

  return { assetToken: data?.getAssetToken?.assetToken };
};
